import type { WorkspaceCurrentRoutesResponse } from "@storyboard/web-client";
import { ApiError, getWorkspaceCurrentRoutes } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { applyCurrentRoutesPage, sweepCurrentRoutes } from "@/models/repositories/CurrentRoute";
import { registryStateStore, type SyncStateStore } from "@/models/sync/cursorStore";
import { createSingleFlight } from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { recordEvent } from "@/services/telemetry";

const log = createLogger("appsync");

/** Drivers the board asks for per page, sent as `limit` on every request. */
export const CURRENT_ROUTES_PAGE_SIZE = 100;

/** Registry key of the `cursor` the last walk ended on. */
export const CURRENT_ROUTES_CURSOR_KEY = "currentRoutesCursor";

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type CurrentRoutesSyncDeps = {
  fetch: (query: { workspaceId: string; cursor?: string; limit: number }) => Promise<WorkspaceCurrentRoutesResponse>;
  cursors: SyncStateStore;
  apply: (workspaceId: string, page: WorkspaceCurrentRoutesResponse) => Promise<void>;
  sweep: (workspaceId: string, named: ReadonlySet<string>) => Promise<void>;
  report: (name: string, context: Record<string, unknown>) => void;
};

/** A walk the server has answered at least one page of and not yet ended. */
type OpenWalk = {
  /** The cursor naming the walk's next page; held in memory only. */
  cursor: string;
  /** The walk's first page's `full`, constant across the walk. */
  full: boolean;
  /** Every membership id the walk's pages have named, which a full walk's sweep keeps. */
  named: Set<string>;
};

/**
 * Binds the current-routes read to the store: a walk of pages, each landing as it arrives.
 *
 * The server decides whether a walk is full or a delta from the cursor it issued; the board sends back whatever it was
 * last given. While an answer's `hasMore` is true, the next page is requested at once with that answer's `cursor`, held
 * in memory; an answer whose `hasMore` is false ends the walk, sweeps the held fleet if the walk was full, and persists
 * its `cursor` for the next tick. Only a walk-ending cursor is persisted, so a board that reloads mid-walk restarts from
 * the last one rather than sweeping against a partial walk.
 *
 * The whole walk is single-flight per workspace: a tick, a resync or a boot landing during one shares it, so two walks
 * never interleave their pages.
 */
export function createCurrentRoutesSync(deps: CurrentRoutesSyncDeps): { sync: (ctx: SyncContext) => Promise<void> } {
  const flights = createSingleFlight();
  const openWalks = new Map<string, OpenWalk>();

  /**
   * The persisted walk-ending cursor, or none.
   *
   * An unreadable registry reads as nothing held, as `SyncScheduler.readCursor` treats it, so it costs one full walk.
   */
  async function persistedCursor(workspaceId: string): Promise<string | undefined> {
    try {
      return await deps.cursors.get(CURRENT_ROUTES_CURSOR_KEY, workspaceId);
    } catch (error) {
      log.warn("[sync] Current routes cursor read failed; starting a full walk", { workspaceId, error });
      return undefined;
    }
  }

  async function walk({ workspaceId }: SyncContext): Promise<void> {
    let cursor = openWalks.get(workspaceId)?.cursor ?? (await persistedCursor(workspaceId));

    for (;;) {
      // A failed page leaves the held fleet, the open walk and the persisted cursor as they are; the next sync re-sends
      // the same cursor, and a page applied twice is harmless.
      let page: WorkspaceCurrentRoutesResponse;
      try {
        page = await deps.fetch({ workspaceId, cursor, limit: CURRENT_ROUTES_PAGE_SIZE });
      } catch (error) {
        const status = error instanceof ApiError ? error.status : undefined;
        // The server refused the cursor: drop it, so the next sync cold-starts.
        if (status === 400) {
          openWalks.delete(workspaceId);
          await deps.cursors.set(CURRENT_ROUTES_CURSOR_KEY, workspaceId, "");
        }
        deps.report("current_routes.refresh_failed", {
          currentRoutes: { workspaceId, status, error: error instanceof Error ? error.message : String(error) },
        });
        return;
      }

      const open = openWalks.get(workspaceId) ?? { cursor: page.cursor, full: page.full, named: new Set<string>() };
      openWalks.set(workspaceId, open);
      await deps.apply(workspaceId, page);
      for (const driver of page.drivers) open.named.add(driver.workspaceMembershipId);

      if (page.hasMore) {
        open.cursor = page.cursor;
        cursor = page.cursor;
        continue;
      }

      if (open.full) await deps.sweep(workspaceId, open.named);
      // Rows land before the cursor that covers them, so an interrupted walk re-reads rather than skips.
      await deps.cursors.set(CURRENT_ROUTES_CURSOR_KEY, workspaceId, page.cursor);
      openWalks.delete(workspaceId);
      return;
    }
  }

  return { sync: (ctx) => flights.join(ctx.workspaceId, () => walk(ctx)) };
}

const production = createCurrentRoutesSync({
  fetch: (query) => getWorkspaceCurrentRoutes(transport, query),
  cursors: registryStateStore,
  apply: applyCurrentRoutesPage,
  sweep: sweepCurrentRoutes,
  report: (name, context) => recordEvent(name, context, "reliability"),
});

/** Brings the workspace's held fleet up to date: a boot, a tick, or a resync. */
export function syncCurrentRoutes(ctx: SyncContext): Promise<void> {
  return production.sync(ctx);
}
